import { Fragment, ComponentType, ReactNode, useRef, useEffect } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { DotsVerticalIcon } from '@heroicons/react/outline'
import { Link } from 'react-router-dom'

export interface ActionItem {
  label?: string
  icon?: ComponentType<{
    className?: string
    'aria-hidden'?: boolean | 'true' | 'false'
  }>
  href?: string
  onClick?: () => void
  show?: boolean
  node?: ReactNode
}

interface Props {
  actions: ActionItem[]
}

export function RowActionsDropdown({ actions }: Props) {
  const visible = actions.filter((a) => a.show !== false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (buttonRef.current?.getAttribute('aria-expanded') === 'true') {
        buttonRef.current.click()
      }
    }
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [])

  if (visible.length === 0) return null

  const getFixedStyle = (): React.CSSProperties => {
    if (!anchorRef.current)
      return { position: 'fixed', top: -9999, left: -9999, zIndex: 9999 }
    const rect = anchorRef.current.getBoundingClientRect()
    // cada item tem ~36px + 8px de padding do container
    const estimatedMenuHeight = visible.length * 36 + 8
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpward = spaceBelow < estimatedMenuHeight + 8

    return {
      position: 'fixed',
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      right: window.innerWidth - rect.right,
      zIndex: 9999
    }
  }

  return (
    <Menu as="div" className="inline-block text-left">
      {({ open }) => (
        <>
          <div ref={anchorRef}>
            <Menu.Button
              ref={buttonRef}
              className="inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-none focus:border-emerald-500"
            >
              <DotsVerticalIcon
                className="h-5 w-5 text-emerald-600"
                aria-hidden="true"
              />
            </Menu.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className="w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
              style={
                open
                  ? getFixedStyle()
                  : { position: 'fixed', top: -9999, left: -9999, zIndex: 9999 }
              }
            >
              <div className="py-1">
                {visible.map((action, i) => (
                  <Menu.Item key={i}>
                    {({ active }) =>
                      action.node ? (
                        <>{action.node}</>
                      ) : action.href ? (
                        <Link
                          to={action.href}
                          className={`flex items-center gap-2 px-4 py-2 text-sm ${
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700'
                          }`}
                        >
                          {action.icon && (
                            <action.icon
                              className="h-4 w-4 text-gray-500"
                              aria-hidden="true"
                            />
                          )}
                          {action.label}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={action.onClick}
                          className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700'
                          }`}
                        >
                          {action.icon && (
                            <action.icon
                              className="h-4 w-4 text-gray-500"
                              aria-hidden="true"
                            />
                          )}
                          {action.label}
                        </button>
                      )
                    }
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  )
}
